import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {fadeInOut, slideInOut} from './animations';
import {Location} from '@angular/common';
import {EventEmitterService} from '../../../services/event-emitter.service';
import {ToastrMessagesProvider} from '../../../services/toastr-messages.service';
import {PaginationService} from 'src/app/services/pagination.service';
import {ImportCsvComponent} from 'src/app/modules/import-csv/import-csv.component';
import {SaveTuningsAndAddonsChangesService} from '../../../services/save-tunings-and-addons-changes.service';
import {lastValueFrom} from 'rxjs';

type AnimationsState = 'close' | 'open';

@Component({
  selector: 'mc-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  animations: [fadeInOut, slideInOut],
})
export class DrawerComponent implements AfterViewInit {
  @ViewChild('closeButton') closeButton: ElementRef;
  @ViewChild('drawerContent') drawerContent: ElementRef;
  @ContentChild('headerTemplate', {static: false})
  headerTemplateRef: TemplateRef<any>;
  animationState: AnimationsState = 'close';
  @Input() goBackUrl: [string, {[key: string]: any}] = null;
  @Input() drawerWidth: string = '925px';
  @Input() notificationDrawer: boolean = false;
  private _opened: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private eventEmitterService: EventEmitterService,
    private location: Location,
    private toastrMessagesProvider: ToastrMessagesProvider,
    private saveTuningsAndAddonsChangesService: SaveTuningsAndAddonsChangesService,
    private pagination: PaginationService,
  ) {}

  @Input()
  set opened(value: boolean) {
    if (value) {
      this._opened = true;
      this.animationState = 'open';
    }
  }

  get opened(): boolean {
    return this._opened;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDownHandler(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  ngAfterViewInit() {
    if (!this.notificationDrawer) {
      this.open();
      // hack for focus close button on open drawer(in default template)
      setTimeout(() => {
        this.closeButton?.nativeElement.focus();
      });
    }
  }

  isOverlapping(element1: Element, element2: Element) {
    const rect1 = element1.getBoundingClientRect();
    const rect2 = element2.getBoundingClientRect();

    return !(
      rect1.right < rect2.left ||
      rect1.left > rect2.right ||
      rect1.bottom < rect2.top ||
      rect1.top > rect2.bottom
    );
  }

  onAnimationDone() {
    if (this.animationState === 'close') {
      this._opened = false;
    }
  }

  back() {
    if (!this.notificationDrawer) {
      this.location.back();
    }
  }

  async close(shouldSwitchContainer: boolean = true) {
    if (await lastValueFrom(this.saveTuningsAndAddonsChangesService.checkIfHasUnsavedTuningAndAddons())) {
      return;
    }
    this.animationState = 'close';
    if (this.goBackUrl) {
      this.router.navigate([this.goBackUrl[0]], {queryParams: this.goBackUrl[1]});
    } else {
      const navigationId = this.location.getState()['navigationId'];
      const isImportCsvComponent = this.route.component === ImportCsvComponent;
      if ((navigationId === 1 || isImportCsvComponent) && !this.notificationDrawer) {
        this.router.navigate([`widget/${this.pagination.currentTab}/p/${this.pagination.currentPage}`], {
          queryParams: this.pagination.getQueryParams(),
        });
      } else {
        this.back();
      }
    }
    this.eventEmitterService.closeDrawer.next(null);
    if (shouldSwitchContainer) {
      this.toastrMessagesProvider.switchToMainContainer();
    }
  }

  open() {
    setTimeout(() => {
      this._opened = true;
      this.animationState = 'open';
      // We add a timeout that will pull the change of variables from the thread,
      // let the view check hook pass, and then change the variable values
    }, 0);
  }

  scrollToTop() {
    if (this.drawerContent) {
      this.drawerContent.nativeElement.scrollTo({top: 0});
    }
  }

  onScroll(): void {
    const statementBtn: Element = document.querySelector('.statement-drawer-button-wrapper');
    const intercomBtn: Element = document.querySelector('.intercom-lightweight-app-launcher');
    if (!statementBtn || !intercomBtn) return;
    if (this.isOverlapping(statementBtn, intercomBtn)) {
      statementBtn.classList.add('move-left');
    } else {
      statementBtn.classList.remove('move-left');
    }
  }
}
